import { forwardRef } from 'react';
import { useTheme } from 'styled-components';
import type { BorderProps, ShadowProps } from 'styled-system';
import { border, shadow } from 'styled-system';
import merge from 'ts-deepmerge';

import type { AppearanceProps, BorderOverrideProps } from '../extensions';
import { appearance, borderShorthandOverride } from '../extensions';
import styledFactory from '../styledFactory';

const SystemCheckbox = styledFactory<
  AppearanceProps &
    Omit<BorderProps, keyof BorderOverrideProps> &
    BorderOverrideProps &
    ShadowProps,
  'input'
>('input', 'SystemCheckbox', undefined, [
  appearance,
  border,
  borderShorthandOverride,
  shadow,
]);

export interface Props
  extends React.ComponentPropsWithRef<typeof SystemCheckbox> {
  checked?: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  variant?: string;
}

const sharedStyles = {
  display: 'inline-block',
  '&:disabled': {
    cursor: 'not-allowed',
  },
};

const CheckboxInput = forwardRef<HTMLInputElement, Props>(
  ({ checked, sx = {}, variant = 'default', onChange, ...props }, ref) => {
    const theme = useTheme();

    const commonStyles = theme.components.checkboxInput?.common || {};
    const variantStyles = theme.components.checkboxInput?.variants[variant];

    if (variantStyles == null) {
      throw new Error(
        `CheckboxInput variant "${variant}" does not exist on this theme.`,
      );
    }

    const styles = merge(sharedStyles, commonStyles, variantStyles, sx);

    return (
      <SystemCheckbox
        ref={ref}
        type="checkbox"
        checked={checked != null ? checked : undefined}
        appearance="none"
        sx={styles}
        onChange={onChange}
        {...props}
      />
    );
  },
);

export default CheckboxInput;
